import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useFieldDecimalPlacesConfig } from 'hooks';
import { useTranslation } from 'react-i18next';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { Autocomplete, IconButton, TextField, Typography } from '@totalsoft/rocket-ui';
import { StyledTableRow, StyledTableBodyCell } from 'components/common/Styles';
import { emptyString } from 'utils/constants';
import { insuranceConventionCommissionValidator } from 'features/insuranceConventions/edit/validators';
import { getErrors, isValid, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_INSURANCE_CONVENTIONS_WRITE } from 'constants/rightsCodes';
import FlexBox from 'components/common/FlexBox';
import { useGlobalIsDirty } from 'features/common/dirtyInfo/hooks';

const InsuranceConventionCommissionListItem = ({
  commission,
  insuranceTypes,
  onAddOrUpdateCommission,
  onDelete,
  onUpdateLocalCommission,
  onCancelAdd
}) => {
  const { cvsChPercent } = useFieldDecimalPlacesConfig();
  const [validation, validate] = useDirtyFieldValidation(insuranceConventionCommissionValidator);
  const editing = useMemo(() => commission.editing || commission.isNew, [commission.editing, commission.isNew]);
  const disabled = useMemo(() => commission.disabled, [commission.disabled]);
  const canWrite = usePermission(ADMIN_INSURANCE_CONVENTIONS_WRITE);

  const updateLocalCommission = useCallback(
    (property, value) => {
      onUpdateLocalCommission(commission.insuranceTypeId, property, value);
    },
    [commission, onUpdateLocalCommission]
  );

  const handleAddOrUpdate = useCallback(
    commission => {
      if (!validate(commission)) return;
      onAddOrUpdateCommission(commission);
    },
    [onAddOrUpdateCommission, validate]
  );

  const handleEdit = useCallback(() => updateLocalCommission('editing', true), [updateLocalCommission]);

  const handleCancelEdit = useCallback(() => {
    if (commission.isNew) onCancelAdd();
    else updateLocalCommission('editing', false);
  }, [commission, onCancelAdd, updateLocalCommission]);

  const handleDelete = useCallback(() => {
    onDelete(commission.productId, commission.insuranceTypeId);
  }, [onDelete, commission]);

  return (
    <StyledTableRow>
      {editing ? (
        <CommissionEditView
          commission={commission}
          validation={validation}
          insuranceTypes={insuranceTypes}
          disabled={disabled || !canWrite}
          formatPercent={cvsChPercent}
          onCancelEdit={handleCancelEdit}
          onAddOrUpdate={handleAddOrUpdate}
          onDelete={handleDelete}
        />
      ) : (
        <CommissionDetailsView
          commission={commission}
          disabled={disabled || !canWrite}
          formatPercent={cvsChPercent}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}
    </StyledTableRow>
  );
};

const CommissionDetailsView = ({ commission, disabled, formatPercent, onEdit, onDelete }) => {
  const { t } = useTranslation();

  return (
    <>
      <StyledTableBodyCell>
        <Typography>{commission.insuranceTypeName}</Typography>
      </StyledTableBodyCell>
      <StyledTableBodyCell>{commission.commission?.toFixed(formatPercent)}</StyledTableBodyCell>
      <StyledTableBodyCell>
        <FlexBox gap={0.5} justifyContent='end'>
          <IconButton type='edit' color='primary' size='tiny' variant='text' onClick={onEdit} tooltip={t('Edit')} disabled={disabled} />
          <IconButton
            type='delete'
            size='tiny'
            variant='text'
            color='primary'
            onClick={onDelete}
            tooltip={t('Delete')}
            disabled={disabled}
          />
        </FlexBox>
      </StyledTableBodyCell>
    </>
  );
};

CommissionDetailsView.propTypes = {
  commission: PropTypes.object,
  disabled: PropTypes.bool,
  formatPercent: PropTypes.number,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func
};

const CommissionEditView = ({ commission, validation, insuranceTypes, disabled, formatPercent, onAddOrUpdate, onDelete, onCancelEdit }) => {
  const { t } = useTranslation();

  const getLensValue = useCallback(
    () => ({
      ...commission,
      initialInsuranceTypeId: commission.insuranceTypeId
    }),
    [commission]
  );

  const [lens, dirtyInfo] = useChangeTrackingLens(getLensValue());
  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  useGlobalIsDirty(isDirty);

  return (
    <>
      <StyledTableBodyCell>
        {
          <Autocomplete
            label={t('InsuranceConventions.Details.Commissions.InsuranceType')}
            simpleValue
            required
            value={lens.insuranceTypeId |> get}
            onChange={lens.insuranceTypeId |> set}
            options={insuranceTypes}
            error={!isValid(validation.insuranceTypeId)}
            helperText={getErrors(validation.insuranceTypeId)}
            disabled={disabled}
          />
        }
      </StyledTableBodyCell>
      <StyledTableBodyCell>
        {
          <TextField
            label={t('InsuranceConventions.Details.Commissions.Commission')}
            isNumeric
            fullWidth
            required
            value={(lens.commission |> get) || emptyString}
            onChange={lens.commission |> set}
            disabled={disabled}
            decimalScale={formatPercent}
            error={!isValid(validation.commission)}
            helperText={getErrors(validation.commission)}
          />
        }
      </StyledTableBodyCell>
      <StyledTableBodyCell>
        <FlexBox gap={0.5} justifyContent='end'>
          <IconButton
            type='save'
            color='primary'
            size='tiny'
            variant='text'
            onClick={() => {
              onAddOrUpdate(lens |> get);
            }}
            tooltip={t('Save')}
            disabled={disabled || !isDirty}
          />
          <IconButton
            type='cancel'
            color='primary'
            size='tiny'
            variant='text'
            onClick={onCancelEdit}
            tooltip={t('Cancel')}
            disabled={disabled}
          />

          {!commission.isNew && (
            <IconButton
              type='delete'
              color='primary'
              size='tiny'
              variant='text'
              onClick={onDelete}
              tooltip={t('Delete')}
              disabled={disabled}
            />
          )}
        </FlexBox>
      </StyledTableBodyCell>
    </>
  );
};

CommissionEditView.propTypes = {
  commission: PropTypes.object,
  validation: PropTypes.object,
  insuranceTypes: PropTypes.array,
  disabled: PropTypes.bool,
  formatPercent: PropTypes.number,
  onAddOrUpdate: PropTypes.func,
  onDelete: PropTypes.func,
  onCancelEdit: PropTypes.func
};

InsuranceConventionCommissionListItem.propTypes = {
  commission: PropTypes.object,
  insuranceTypes: PropTypes.array,
  onDelete: PropTypes.func,
  onAddOrUpdateCommission: PropTypes.func,
  onUpdateLocalCommission: PropTypes.func,
  onCancelAdd: PropTypes.func
};

export default InsuranceConventionCommissionListItem;
